import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Form, FormDropdown, FormTextArea, Button } from '@fluentui/react-northstar';
import Loader from 'components/Loader';
import ErrorMessage from 'components/ErrorMessage';
import { useQuery, useMutation } from 'react-query';
import PropTypes from 'prop-types';
import { getRecognitionData, enhanceMessage } from 'Services/apiFunctions';
import { GET_RECOGNITION_DATA } from 'Services/apiKeys';
import { dialog } from '@microsoft/teams-js';
import { partition } from 'lodash';
import { capitalizeFLetter, getTeamsChannelName, showSnackBarMessage } from 'utils/helperFunctions';
import { useToaster } from 'context/SnackbarContext';

const recognizeTypeLabelId = 'recognize-type-label';
const recognizeMemberLabelId = 'recognize-member-label';
const recognizeValueLabelId = 'recognize-value-label';
const recognizeChannelLabelId = 'recognize-channel-label';
const RESTRICTED_USER_NOTE = 'This user has been restricted from receiving recognitions by the admin.';

const RecognitionForm = ({ authToken, setCreditBalance, setSubmitLoader, messageId, creditBalance }) => {
    const navigate = useNavigate();
    const { SetSnackbar } = useToaster();
    const [coreValues, setCoreValues] = useState({ data: [], isFetching: true });
    const [rewards, setRewards] = useState({ data: [], isFetching: true });
    const [members, setMembers] = useState({ data: [], isFetching: true });
    const [channel, setChannel] = useState({ data: [], isFetching: true });
    const [rewardSelector, setRewardSelector] = useState(null);
    const [compValueSelector, setCompValueSelector] = useState(null);
    const [reason, setReason] = useState('');
    const [userSelector, setUserSelector] = useState([]);
    const [channelSelector, setchannelSelector] = useState({});
    const [charLimit, setCharLimit] = useState(0);
    const [disableButton, setDisableButton] = useState(false);
    const [isAiEnhanced, setIsAiEnhanced] = useState(false);
    const [aiEnhancMessage, setAiEnhancMessage] = useState('');
    const [originalRecognitionMessage, setOriginalRecognitionMessage] = useState();

    const isCompanyValueRequired = useRef();
    const [memberDropDown, setMemberDropDown] = useState(false);
    const [error, setError] = useState({
        type: false,
        user: false,
        message: false,
        channel: false,
        companyValue: false,
        inSufficientBalance: false,
    });

    const { data: recognitionDetails } = useQuery([GET_RECOGNITION_DATA, authToken, messageId], getRecognitionData, {
        onError: (err) => navigate('/modals/inactive'),
        onSuccess: (data) => {
            const recognitionData = data.data;
            isCompanyValueRequired.current = recognitionData.isCompanyValueRequired;
            if (messageId) {
                const addOnData = recognitionData.preFilledData;
                let selectedUser = addOnData.users.map((option) => ({
                    header: option.userName,
                    id: option._id,
                }));
                setUserSelector(selectedUser);
                if (addOnData.rewardType) {
                    let selectedReward = {
                        header: `${capitalizeFLetter(addOnData.rewardType.name)} (${
                            addOnData.rewardType.points
                        } Points)`,
                        id: addOnData.rewardType._id,
                    };
                    setRewardSelector(selectedReward);
                }
            }

            let rewardList = recognitionData.rewardTypes.reduce((list, option) => {
                return [
                    ...list,
                    {
                        header: `${capitalizeFLetter(option.name)} (${option.points} Points)`,
                        id: option._id,
                        points: option.points,
                    },
                ];
            }, []);
            let memberList = recognitionData.users.reduce((list, member) => {
                const isMemberDisabled = member.permissions?.recognition?.restricted;
                return [
                    ...list,
                    {
                        header: member.userName,
                        ...(isMemberDisabled && {
                            content: RESTRICTED_USER_NOTE,
                            styles: { opacity: '0.6' },
                        }),
                        disabled: isMemberDisabled,
                        id: member._id,
                    },
                ];
            }, []);

            /* The code is using the `partition` function to split the `memberList` array into two
            separate arrays based on a condition. In this case, the condition is checking if the
            `disabled` property of each member is false. The `enabledMembers` array will contain all
            members where `disabled` is false, and the `disabledMembers` array will contain all
            members where `disabled` is true. Finally, the code concatenates the two arrays back
            together and assigns the result back to the `memberList` variable. */
            const [enabledMembers, disabledMembers] = partition(memberList, (member) => !member.disabled);
            memberList = [...enabledMembers, ...disabledMembers];

            let valueList = recognitionData.companyValues.reduce((list, option) => {
                return [...list, { header: option.valueStr, id: option._id }];
            }, []);
            let channelList = recognitionData.channels.reduce((list, option) => {
                return [
                    ...list,
                    {
                        header: `${option.groupName} -> ${option.channelName}`,
                        ...option,
                    },
                ];
            }, []);

            setCreditBalance(recognitionData?.creditBalance);
            setRewards({ data: rewardList, isFetching: false });
            setMembers({ data: memberList, isFetching: false });
            setCoreValues({ data: valueList, isFetching: false });
            setChannel({ data: channelList, isFetching: false });
        },
    });
    const { mutateAsync: handleEnhanceMessage, isLoading: isEnhancingMessage } = useMutation(enhanceMessage);

    const validateForm = () => {
        setError({
            type: !rewardSelector?.id,
            user: userSelector.length < 1 || userSelector?.some((obj) => obj.id === recognitionDetails.data?.userId),
            message: !messageId && !reason,
            channel:
                !messageId && !recognitionDetails?.data?.defaultRecognitionChannel
                    ? !Object.keys(channelSelector).length > 0
                    : false,
            companyValue: isCompanyValueRequired.current && !compValueSelector,
            inSufficientBalance: rewardSelector?.points * userSelector?.length > creditBalance,
        });
        return (
            !rewardSelector ||
            userSelector.length < 1 ||
            userSelector?.some((obj) => obj.id === recognitionDetails.data?.userId) ||
            (!messageId && !reason) ||
            (!messageId && !recognitionDetails?.data?.defaultRecognitionChannel
                ? !Object.keys(channelSelector).length > 0
                : false) ||
            (isCompanyValueRequired.current && !compValueSelector) ||
            rewardSelector?.points * userSelector?.length > creditBalance
        );
    };

    const handleSubmit = () => {
        if (validateForm()) {
            return;
        }
        setDisableButton(true);
        setSubmitLoader(true);
        let data = {
            reward: rewardSelector.id,
            companyValue: compValueSelector,
            reason,
            users: userSelector.map((item) => item.id),
            ...(messageId && { messageId }),
            ...(!messageId && { channel: recognitionDetails?.data?.defaultRecognitionChannel || channelSelector }),
        };
        dialog.url.submit(
            { data: data, startedVia: 'messagingExtension', flagType: 'recognitions_submit' },
            process.env.REACT_APP_TEAMS_APP_ID
        );
    };

    const handleRecognizeMemberSelection = (_event, { value }) => {
        if (!value) {
            setUserSelector('');
        } else {
            setUserSelector(value);
            if (error.user && value) {
                setError({ ...error, user: false });
            }
        }
    };
    const handleRecognizeChannelSelection = (_event, { value }) => {
        setchannelSelector(value);
        if (error.channel && value) {
            setError({ ...error, channel: false });
        }
    };
    const handleRecognizeValueSelection = (_event, { value }) => {
        if (!value) {
            setCompValueSelector('');
        } else {
            setCompValueSelector(value.id);
            if (error.companyValue) {
                setError({ ...error, companyValue: false });
            }
        }
    };

    const handleTypeChange = (_event, { value }) => {
        setRewardSelector(value);
        if (error.type) {
            setError({ ...error, type: false });
        }
    };
    const handleErrorMessage = (message) => {
        if (userSelector?.some((obj) => obj.id === recognitionDetails.data?.userId)) {
            return <ErrorMessage message={"You can't recognize yourself"} />;
        } else {
            return <ErrorMessage />;
        }
    };
    const handleRevertOriginalRecognitionMessage = () => {
        setReason(originalRecognitionMessage);
        setIsAiEnhanced(false);
    };

    const handleAIEnhanceMessage = async () => {
        try {
            if (originalRecognitionMessage !== reason) {
                const aiEnhancedMessage = await handleEnhanceMessage({ reason, authToken });
                setOriginalRecognitionMessage(reason);
                setAiEnhancMessage(aiEnhancedMessage);
                setReason(aiEnhancedMessage);
                setIsAiEnhanced(true);
            } else {
                setReason(aiEnhancMessage);
                setIsAiEnhanced(true);
            }
        } catch (err) {
            showSnackBarMessage(SetSnackbar, 'error', err?.message);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            <FormDropdown
                label={{
                    content: <div className='subtitle-4 margin-bottom-8'>Type of recognition</div>,
                    id: recognizeTypeLabelId,
                }}
                value={rewardSelector}
                className={error.type ? 'red-border' : ''}
                items={rewards.data}
                aria-labelledby={recognizeTypeLabelId}
                placeholder='Select'
                loading={rewards.isFetching}
                loadingMessage={<Loader />}
                onChange={handleTypeChange}
                fluid
            />
            {error.type && handleErrorMessage()}
            <FormDropdown
                label={{
                    content: <div className='subtitle-4 margin-bottom-8'>Who do you want to recognize?</div>,
                    id: recognizeMemberLabelId,
                }}
                open={memberDropDown}
                items={members.data}
                value={userSelector}
                multiple
                onBlur={() => setMemberDropDown(false)}
                onFocus={() => setMemberDropDown(true)}
                onChange={handleRecognizeMemberSelection}
                aria-labelledby={recognizeMemberLabelId}
                className={error.user ? 'red-border' : ''}
                placeholder='Type or select someone'
                fluid
                search
            />
            {error.user && handleErrorMessage()}
            <FormTextArea
                className={clsx({ 'recognize-modal-message': true, 'red-border': error.message })}
                name='recognize-modal-message'
                id='recognize-modal-message'
                maxLength={3000}
                value={reason}
                label={
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div className='subtitle-4 margin-bottom-8'>
                            Why do you want to recognize?{messageId && <span className='body-2'>(Optional)</span>}
                            <span className='body-2'>{`(${charLimit}/3000 Chars)`}</span>
                        </div>
                        <Button
                            content={isAiEnhanced ? 'Revert to original message' : 'Enhance message with AI'}
                            loading={isEnhancingMessage}
                            size='small'
                            onClick={(e) => {
                                e.preventDefault();
                                if (isAiEnhanced) {
                                    handleRevertOriginalRecognitionMessage();
                                } else {
                                    handleAIEnhanceMessage();
                                }
                            }}
                        />
                    </div>
                }
                placeholder='Write Something'
                onChange={(_event, { value }) => {
                    setReason(value);
                    setCharLimit(value.length);
                    if (error.message && value) {
                        setError({ ...error, message: false });
                    }
                }}
                fluid
            />
            {error.message && handleErrorMessage()}
            {coreValues.data.length > 0 && (
                <FormDropdown
                    label={{
                        content: (
                            <div className='subtitle-4 margin-bottom-8'>
                                Company value
                                {!isCompanyValueRequired.current && <span className='body-2'>(Optional)</span>}
                            </div>
                        ),
                        id: recognizeValueLabelId,
                    }}
                    placeholder='Select'
                    items={coreValues.data}
                    aria-labelledby={recognizeValueLabelId}
                    className={error.companyValue ? 'red-border' : ''}
                    fluid
                    loading={coreValues.isFetching}
                    loadingMessage={<Loader />}
                    onChange={handleRecognizeValueSelection}
                />
            )}
            {error.companyValue && handleErrorMessage()}
            {!messageId && !recognitionDetails?.data?.defaultRecognitionChannel && (
                <FormDropdown
                    label={{
                        content: (
                            <div className='subtitle-4 margin-bottom-8'>
                                Where do you want to post your recognition?
                            </div>
                        ),
                        id: recognizeChannelLabelId,
                    }}
                    items={channel.data}
                    onChange={handleRecognizeChannelSelection}
                    aria-labelledby={recognizeChannelLabelId}
                    className={error.channel ? 'red-border' : ''}
                    placeholder='Type or select channel'
                    fluid
                    search
                />
            )}
            {error.channel && handleErrorMessage()}
            <div className='body-2'>
                {recognitionDetails?.data?.defaultRecognitionChannel && (
                    <>
                        <b>Note:</b>
                        <span>
                            Recognition will be sent out in{' '}
                            <strong>{getTeamsChannelName(recognitionDetails?.data?.defaultRecognitionChannel)}</strong>
                        </span>
                    </>
                )}
                {error.inSufficientBalance && <ErrorMessage message='Insufficient balance' />}
            </div>

            <div className='modal-button-container'>
                <button disabled={disableButton}>Submit</button>
            </div>
        </Form>
    );
};
RecognitionForm.propTypes = {
    authToken: PropTypes.string,
    setCreditBalance: PropTypes.func,
    setSubmitLoader: PropTypes.func,
    messageId: PropTypes.string,
    creditBalance: PropTypes.number,
};

export default RecognitionForm;
